import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Instagram, Facebook, Youtube, Sparkles, Zap, Gem, Wand2, Film } from "lucide-react";

// Hardcoded fallback matching /api/video-styles — shown instantly so the dropdown
// is never empty even if the network call hasn't finished (or silently fails).
const DEFAULT_STYLE_CATALOG = [
  { key: "cinematic", label: "Cinematic Pro" },
  { key: "vintage", label: "Vintage VHS" },
  { key: "anime", label: "Anime / Ghibli" },
  { key: "cyberpunk", label: "Cyberpunk Neon" },
  { key: "synthwave", label: "Retro Synthwave" },
  { key: "bw_art", label: "B&W Art Film" },
  { key: "abstract", label: "Abstract Motion" },
];

const PLATFORMS = [
  { id: "instagram_reel", label: "Instagram Reel", Icon: Instagram, platform: "instagram", defaultType: "reel", peakHour: 19 },
  { id: "instagram_post", label: "Instagram Post", Icon: Instagram, platform: "instagram", defaultType: "post", peakHour: 11 },
  { id: "facebook",       label: "Facebook Post",  Icon: Facebook,  platform: "facebook",  defaultType: "post", peakHour: 13 },
  { id: "youtube",        label: "YouTube Short",  Icon: Youtube,   platform: "youtube",   defaultType: "short", peakHour: 17 },
];

// Google Veo 3.1 through the Emergent Universal Key — the only video engine.
const RENDER_SPEEDS = [
  { id: "fast", label: "Veo 3.1 Fast", Icon: Zap, hint: "Google Veo 3.1 Fast · 8s native 1080p clip, looped to your length · ~1-3 min", recommended: true },
  { id: "pro",  label: "Veo 3.1 Pro",  Icon: Gem, hint: "Google Veo 3.1 · 8s native 1080p · highest fidelity · ~3-6 min" },
];

export default function CustomPostDialog({ open, onOpenChange, onCreated }) {
  const [platformPick, setPlatformPick] = useState("instagram_reel");  // PLATFORMS[].id
  const platformMeta = PLATFORMS.find((p) => p.id === platformPick) || PLATFORMS[0];
  const platform = platformMeta.platform;   // actual backend platform key
  const contentType = platformMeta.defaultType;  // reel | post | short
  const [caption, setCaption] = useState("");
  const [releaseTitle, setReleaseTitle] = useState("");
  const [hashtags, setHashtags] = useState("#melodichouse #alchemist");
  const [scheduledAt, setScheduledAt] = useState("");
  const [videoMode, setVideoMode] = useState("none"); // "none" | "kenburns" | "ai" | "upload"
  const [aiStyle, setAiStyle] = useState("auto");
  const [duration, setDuration] = useState(15); // video length seconds — 15s minimum for IG/TikTok algo reach
  const [renderSpeed, setRenderSpeed] = useState("fast");
  const [styleCatalog, setStyleCatalog] = useState(DEFAULT_STYLE_CATALOG);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [suggestingTags, setSuggestingTags] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [llmStatus, setLlmStatus] = useState(null); // null | {ok, detail}
  const [videoPrompt, setVideoPrompt] = useState("");
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/video-styles").then((r) => setStyleCatalog(r.data.styles || [])).catch(() => {});
  }, [open]);

  // Pre-flight probe whenever the user switches to AI mode — warn upfront if
  // the Universal Key is depleted so we don't queue a doomed background job.
  useEffect(() => {
    if (!open || videoMode !== "ai") {
      setLlmStatus(null);
      return;
    }
    setLlmStatus({ checking: true });
    api.get("/llm-status")
      .then((r) => setLlmStatus(r.data))
      .catch(() => setLlmStatus(null));
  }, [open, videoMode]);

  const reset = () => {
    setPlatformPick("instagram_reel");
    setCaption("");
    setReleaseTitle("");
    setHashtags("#melodichouse #alchemist");
    setScheduledAt("");
    setVideoMode("none");
    setAiStyle("auto");
    setDuration(15);
    setRenderSpeed("fast");
    setVideoPrompt("");
    setImage(null);
    setPreview(null);
    setAudioFile(null);
    setVideoFile(null);
    setUploadPct(0);
  };

  const pickVideo = (f) => {
    if (!f) return;
    if (f.size > 500 * 1024 * 1024) {
      toast.error("Video too large (max 500MB)");
      return;
    }
    setVideoFile(f);
  };

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Image too large (max 20MB)");
      return;
    }
    setImage(f);
    const r = new FileReader();
    r.onload = (e) => setPreview(e.target.result);
    r.readAsDataURL(f);
  };

  useEffect(() => {
    // IG Post = photo feed post. Video is irrelevant — force it off.
    if (contentType === "post" && platform === "instagram" && videoMode !== "none") {
      setVideoMode("none");
    }
  }, [contentType, platform, videoMode]);

  const switchVideoMode = (mode) => {
    setVideoMode(mode);
    // Snap duration to whichever set the new mode supports — minimum 15s
    // everywhere so social-algorithm reach isn't capped by short clips.
    if (mode === "ai" && ![15, 20, 30].includes(duration)) setDuration(15);
    if (mode === "kenburns" && ![15, 30].includes(duration)) setDuration(15);
    if (mode === "none") { setAudioFile(null); setVideoFile(null); }
    if (mode !== "upload") setVideoFile(null);
  };

  const suggestTags = async () => {
    setSuggestingTags(true);
    try {
      const r = await api.post("/hashtag-suggest", {
        track_title: releaseTitle || undefined,
        count: 15,
      });
      const joined = (r.data.hashtags || []).join(" ");
      setHashtags(joined);
      const src = r.data.source === "ai" ? "AI + melodic-house seed pool" : "genre preset";
      toast.success(`${(r.data.hashtags || []).length} hashtags suggested · ${src}.`);
    } catch {
      toast.error("Suggestion failed");
    } finally {
      setSuggestingTags(false);
    }
  };

  const pickAudio = (f) => {
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) {
      toast.error("Audio too large (max 200MB)");
      return;
    }
    setAudioFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!caption.trim()) {
      toast.error("Caption is required");
      return;
    }
    if (videoMode === "kenburns" && !image) {
      toast.error("Upload artwork to render a Ken-Burns video, or pick an AI style.");
      return;
    }
    if (videoMode === "upload" && !videoFile) {
      toast.error("Upload a video file to use this mode.");
      return;
    }
    if (contentType === "post" && !image) {
      toast.error("Instagram Post needs an image. Upload artwork above.");
      return;
    }
    setSubmitting(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      if (image) fd.append("image", image);
      if (audioFile && videoMode !== "none") fd.append("audio", audioFile);
      if (videoFile && videoMode === "upload") fd.append("video", videoFile);
      fd.append("caption", caption);
      fd.append("platform", platform);
      fd.append("content_type", contentType);
      fd.append("release_title", releaseTitle || "");
      fd.append("hashtags", hashtags || "");
      // Scheduling — user picks a date only; backend auto-applies the best
      // posting HOUR for this platform+content_type via _peak_hour.
      if (scheduledAt) fd.append("schedule_date", scheduledAt); // YYYY-MM-DD from <input type=date>
      fd.append("generate_video", String(videoMode === "kenburns" && !!image));
      if (videoMode === "ai") fd.append("ai_video_style", aiStyle);
      if (videoMode === "ai") fd.append("render_speed", renderSpeed);
      if (videoMode === "ai" && videoPrompt.trim()) fd.append("video_prompt", videoPrompt.trim());
      if (videoMode !== "none" && videoMode !== "upload") fd.append("duration", String(duration));

      await api.post("/drafts/custom", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000,
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      const audioNote = audioFile ? " Your audio will be muxed in once the video finishes." : "";
      const engineLabels = { fast: "Veo 3.1 Fast", pro: "Veo 3.1 Pro" };
      const engineEtas   = { fast: "1-3 min", pro: "3-6 min" };
      const eng = engineLabels[renderSpeed] || "Veo 3.1";
      const eta = engineEtas[renderSpeed] || "a few min";
      const successMsg = videoMode === "ai"
        ? `Draft saved. ${eng} is rendering your ${duration}s video in the background (${eta}) — check the Queue.${audioNote}`
        : videoMode === "upload"
        ? `Draft saved with your uploaded video.${audioFile ? " Audio muxed in." : ""}`
        : `Custom draft created.${audioNote}`;
      toast.success(successMsg);
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to create draft";
      toast.error(typeof detail === "string" ? detail : "Failed to create draft");
    } finally {
      setSubmitting(false);
      setUploadPct(0);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm w-[calc(100vw-1rem)] max-w-3xl text-white max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="custom-post-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">
            New custom post
          </DialogTitle>
          <DialogDescription className="label-mono text-white/45">
            Compose a one-off Instagram / Facebook / YouTube post — text, artwork,
            optional Ken-Burns or AI-generated video, and your own music track.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-6 mt-3">
          {/* Left — image upload */}
          <div>
            <div className="label-mono mb-2">Artwork</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="custom-post-file-input"
            />
            {preview ? (
              <div className="relative group">
                <img src={preview} alt="Custom artwork preview" className="w-full aspect-square object-cover border border-white/10" />
                <button
                  type="button"
                  className="absolute top-2 right-2 bg-black/80 border border-white/20 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => { setImage(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                  data-testid="custom-post-remove-image"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full aspect-square border border-dashed border-white/20 flex flex-col items-center justify-center gap-3 text-white/55 hover:text-white hover:border-[#FF3B00] transition-colors"
                data-testid="custom-post-upload-btn"
              >
                <Upload size={28} strokeWidth={1.2} />
                <div className="label-mono">Upload artwork</div>
                <div className="text-xs text-white/40">JPG / PNG / WebP · max 20MB</div>
              </button>
            )}
            <label className="flex items-center gap-3 mt-4 cursor-pointer text-sm text-white/75">
              <input
                type="radio"
                name="video-mode"
                checked={videoMode === "none"}
                onChange={() => switchVideoMode("none")}
                className="accent-[#FF3B00]"
              />
              No video — image only
            </label>
            {contentType === "post" && platform === "instagram" && (
              <div className="label-mono text-white/45 mt-2 leading-relaxed p-3 border border-white/10 bg-white/[0.02]">
                Instagram Post is a photo feed post — upload artwork above and hit publish. No video options apply.
              </div>
            )}
            {!(contentType === "post" && platform === "instagram") && (
            <>
            <label className="flex items-center gap-3 mt-2 cursor-pointer text-sm text-white/75">
              <input
                type="radio"
                name="video-mode"
                checked={videoMode === "kenburns"}
                onChange={() => switchVideoMode("kenburns")}
                disabled={!image}
                data-testid="custom-post-video-kenburns"
                className="accent-[#FF3B00]"
              />
              Ken-Burns zoom on this artwork <span className="text-white/35">(fast, ~5s)</span>
            </label>
            <label className="flex items-center gap-3 mt-2 cursor-pointer text-sm text-white/75">
              <input
                type="radio"
                name="video-mode"
                checked={videoMode === "ai"}
                onChange={() => switchVideoMode("ai")}
                data-testid="custom-post-video-ai"
                className="accent-[#FF3B00]"
              />
              <Sparkles size={14} className="text-[#FF3B00]" />
              AI-generated video <span className="text-white/35">(Google Veo 3.1 · describe it or let the style decide)</span>
            </label>
            <label className="flex items-center gap-3 mt-2 cursor-pointer text-sm text-white/75">
              <input
                type="radio"
                name="video-mode"
                checked={videoMode === "upload"}
                onChange={() => switchVideoMode("upload")}
                data-testid="custom-post-video-upload"
                className="accent-[#FF3B00]"
              />
              <Upload size={14} className="text-[#FF3B00]" />
              Upload my own video <span className="text-white/35">(mp4 / mov / webm, max 500MB)</span>
            </label>

            {videoMode === "upload" && (
              <div className="mt-3 p-4 border border-white/10 bg-white/[0.02]">
                <input
                  ref={videoRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
                  className="hidden"
                  onChange={(e) => pickVideo(e.target.files?.[0])}
                  data-testid="custom-post-video-input"
                />
                {videoFile ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs font-mono text-white/80 min-w-0">
                      <Film size={14} className="text-[#FF3B00] shrink-0" />
                      <span className="truncate">{videoFile.name}</span>
                      <span className="text-white/40 shrink-0">· {(videoFile.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setVideoFile(null); if (videoRef.current) videoRef.current.value = ""; }}
                      className="text-xs text-white/45 hover:text-white shrink-0"
                      data-testid="custom-post-video-remove"
                    >
                      remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => videoRef.current?.click()}
                    className="w-full p-3 border border-dashed border-white/20 hover:border-white/45 hover:text-white text-white/55 text-sm flex items-center justify-center gap-2 transition-colors"
                    data-testid="custom-post-video-pick"
                  >
                    <Upload size={14} /> Choose video file
                  </button>
                )}
                <div className="label-mono text-white/35 mt-2 leading-relaxed">
                  Already-edited promo clip? Drop it in. We&rsquo;ll skip AI generation and use your video as-is. If you also attach audio below, we&rsquo;ll mux it onto your clip before posting.
                </div>
              </div>
            )}

            {videoMode !== "none" && videoMode !== "upload" && (
              <div className="mt-3 flex items-center gap-3 text-sm text-white/75 flex-wrap">
                <span className="label-mono text-white/45">Length:</span>
                {(videoMode === "ai" ? [15, 20, 30] : [15, 30]).map((d) => (
                  <button
                    type="button"
                    key={d}
                    onClick={() => setDuration(d)}
                    data-testid={`custom-post-duration-${d}`}
                    className={`px-3 py-1 border text-xs font-mono transition-colors ${
                      duration === d
                        ? "border-[#FF3B00] text-white bg-[#FF3B00]/10"
                        : "border-white/15 text-white/60 hover:text-white hover:border-white/30"
                    }`}
                  >
                    {d}s
                  </button>
                ))}
                {videoMode === "ai" && (
                  <span className="label-mono text-white/35">
                    Native clip auto-loops to {duration}s · 15s minimum for IG/TikTok algo reach
                  </span>
                )}
              </div>
            )}

            {videoMode === "ai" && (
              <div className="mt-4 p-4 border border-white/10 bg-white/[0.02]">
                <div className="label-mono mb-2">Describe the video you want</div>
                <textarea
                  className="mp-textarea min-h-[90px]"
                  value={videoPrompt}
                  onChange={(e) => setVideoPrompt(e.target.value.slice(0, 1200))}
                  placeholder="e.g. Slow drone shot over Amsterdam canals at dawn, mist on the water, warm streetlights fading, a lone figure walking along the bridge…"
                  data-testid="custom-post-video-prompt"
                />
                <div className="label-mono text-white/35 mt-2 leading-relaxed">
                  Optional. Leave empty and we build the scene from your release + visual style. Your uploaded track (below) is synced onto the finished clip.
                </div>
                <div className="label-mono mt-4 mb-2">Render quality · Google Veo 3.1 via Universal Key</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {RENDER_SPEEDS.map(({ id, label, Icon, hint, recommended }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setRenderSpeed(id)}
                      data-testid={`render-speed-${id}`}
                      className={`relative p-2.5 border flex items-center gap-3 transition-colors text-left ${
                        renderSpeed === id
                          ? "border-[#FF3B00] bg-[#FF3B00]/10 text-white"
                          : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                      }`}
                    >
                      {recommended && (
                        <span className="absolute -top-2 left-3 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#FF3B00] text-black">
                          Default
                        </span>
                      )}
                      <Icon size={14} strokeWidth={1.4} className="shrink-0" />
                      <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
                    </button>
                  ))}
                </div>
                <div className="label-mono text-white/35 mt-2 leading-relaxed">
                  {RENDER_SPEEDS.find((s) => s.id === renderSpeed)?.hint}
                </div>
              </div>
            )}

            {videoMode === "ai" && (
              <div className="mt-4 p-4 border border-white/10 bg-white/[0.02]">
                <div className="label-mono mb-2">Visual style</div>
                <select
                  className="mp-input"
                  value={aiStyle}
                  onChange={(e) => setAiStyle(e.target.value)}
                  data-testid="custom-post-ai-style"
                >
                  <option value="auto">Auto (rotate)</option>
                  {styleCatalog.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <div className="label-mono text-white/35 mt-2 leading-relaxed">
                  Color palette is locked to vintage black / grey / white across every style.
                  Set your artist description in Settings for better results.
                </div>
                {llmStatus && !llmStatus.checking && (
                  llmStatus.ok ? (
                    <div className="mt-3 flex items-center gap-2 text-xs font-mono text-[#00FF66]" data-testid="llm-status-ok">
                      <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full" />
                      Universal Key online · Veo 3.1 ready
                    </div>
                  ) : (
                    <div className="mt-3 p-3 border border-[#FF3B00]/40 bg-[#FF3B00]/10 text-[#FFB199] text-xs leading-relaxed" data-testid="llm-status-bad">
                      <div className="font-mono uppercase tracking-wider text-[#FF3B00] mb-1">⚠ Universal Key issue</div>
                      {llmStatus.detail || "Universal Key probe failed."}
                    </div>
                  )
                )}
              </div>
            )}

            {videoMode !== "none" && (
              <div className="mt-4 p-4 border border-white/10 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-mono">Custom audio (optional)</div>
                  {audioFile && (
                    <button
                      type="button"
                      onClick={() => { setAudioFile(null); if (audioRef.current) audioRef.current.value = ""; }}
                      className="text-xs text-white/45 hover:text-white"
                      data-testid="custom-post-audio-remove"
                    >
                      remove
                    </button>
                  )}
                </div>
                <input
                  ref={audioRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/wave,audio/mp4,audio/m4a,audio/x-m4a,audio/aac,audio/ogg,audio/flac"
                  className="hidden"
                  onChange={(e) => pickAudio(e.target.files?.[0])}
                  data-testid="custom-post-audio-input"
                />
                {audioFile ? (
                  <div className="text-sm text-white/80 truncate" data-testid="custom-post-audio-name">
                    {audioFile.name} <span className="text-white/35">· {(audioFile.size / (1024 * 1024)).toFixed(1)}MB</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => audioRef.current?.click()}
                    className="w-full border border-dashed border-white/15 px-3 py-3 text-xs text-white/55 hover:text-white hover:border-[#FF3B00] transition-colors"
                    data-testid="custom-post-audio-pick"
                  >
                    Upload your music · MP3 / WAV / M4A · max 200MB
                  </button>
                )}
                <div className="label-mono text-white/35 mt-2 leading-relaxed">
                  We&rsquo;ll mux this onto the rendered video automatically. Trim handles appear after the video finishes.
                </div>
              </div>
            )}
            </>
            )}
          </div>

          {/* Right — fields */}
          <div className="space-y-5">
            <div>
              <div className="label-mono mb-2">Platform & format</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PLATFORMS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setPlatformPick(id)}
                    data-testid={`custom-post-platform-${id}`}
                    className={`p-3 border flex flex-col items-center gap-1.5 transition-colors ${
                      platformPick === id
                        ? "border-[#FF3B00] bg-[#FF3B00]/10 text-white"
                        : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                    }`}
                  >
                    <Icon size={16} strokeWidth={1.4} />
                    <span className="text-[11px] font-mono uppercase tracking-wider text-center leading-tight">{label.replace("Instagram ", "IG ").replace("Facebook ", "FB ").replace("YouTube ", "YT ")}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">Release / track title (optional)</div>
              <input
                type="text"
                className="mp-input"
                placeholder="e.g. Midnight Cathedral"
                value={releaseTitle}
                onChange={(e) => setReleaseTitle(e.target.value)}
                data-testid="custom-post-title-input"
              />
            </div>

            <div>
              <div className="label-mono mb-2">Caption</div>
              <textarea
                className="mp-textarea"
                rows={5}
                placeholder="Write your own caption… or start typing and keep it short."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                required
                data-testid="custom-post-caption-textarea"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono">Hashtags</div>
                <button
                  type="button"
                  onClick={suggestTags}
                  disabled={suggestingTags}
                  className="label-mono text-[#FF3B00] hover:text-[#FF6E3F] flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                  data-testid="custom-post-suggest-tags"
                >
                  <Wand2 size={11} />
                  {suggestingTags ? "Thinking…" : "Suggest with AI"}
                </button>
              </div>
              <input
                type="text"
                className="mp-input"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                data-testid="custom-post-hashtags-input"
              />
            </div>

            <div>
              <div className="label-mono mb-2">Schedule date (optional — time auto-set to best posting hour for {contentType === "reel" ? "IG Reel · 7pm" : contentType === "short" ? "YT Short · 5pm" : platform === "instagram" ? "IG Post · 11am" : "FB Post · 1pm"})</div>
              <input
                type="date"
                className="mp-input"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="custom-post-schedule-input"
              />
            </div>
          </div>
        </form>

        <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-white/10 items-center">
          {submitting && uploadPct > 0 && uploadPct < 100 && (
            <div className="flex-1 max-w-xs" data-testid="custom-post-upload-progress">
              <div className="label-mono text-white/45 mb-1">Uploading {uploadPct}%</div>
              <div className="h-1 bg-white/10 overflow-hidden">
                <div className="h-full bg-[#FF3B00] transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          )}
          <button
            type="button"
            className="btn-ghost-line"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="custom-post-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-amber disabled:opacity-60"
            onClick={onSubmit}
            disabled={submitting || (videoMode === "ai" && llmStatus && llmStatus.ok === false)}
            data-testid="custom-post-submit"
          >
            {submitting ? "Creating…" : "Create draft"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
